import React from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import type {
  CreativeDecisionPortfolioResult,
  CreativeDecisionPortfolioRequest,
  CreativeDecisionPortfolioSort,
  CreativeDeploymentScope,
  CreativeDecisionProjection,
  CreativeLearningOperationsReport,
} from '../creativeDecisions';
import {
  CreativeDecisionPortfolio,
  EMPTY_CREATIVE_DECISION_PORTFOLIO_FILTERS,
  type CreativeDecisionPortfolioFilters,
} from '../components/creativeDecisions/CreativeDecisionPortfolio';
import { CREATIVE_REPORT_LOCALE_ALL } from '../components/creativeDecisions/CreativeLocaleAnalysis';
import { CreativeLearningOperationsSummary } from '../components/creativeDecisions/CreativeLearningOperationsSummary';
import { useCreativeReportEnrichment } from '../components/creativeDecisions/useCreativeReportEnrichment';
import { useCreativeProviderSourceEnrichment } from '../components/creativeDecisions/useCreativeProviderSourceEnrichment';
import { CreativeDiagnosticsSavedView, CreativePerformanceSavedView } from '../components/creativeReports/CreativeReportSavedViews';
import { CREATIVE_DECISIONS_TEST_IDS } from '../testIds';
import { Tab, Tabs } from '@mui/material';

/** W-R6 (DISC-AC-149/150): the saved-view axis — deep-linkable via `?view=`. */
type CreativeDecisionsView = 'decisions' | 'performance' | 'diagnostics';

function parseView(value: string | null): CreativeDecisionsView {
  return value === 'performance' || value === 'diagnostics' ? value : 'decisions';
}

export function CreativeDecisionsPage(): React.ReactElement {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultRange = React.useMemo(defaultDateRange, []);
  const [draftFrom, setDraftFrom] = React.useState(() => dateOnly(searchParams.get('from')) ?? defaultRange.from);
  const [draftTo, setDraftTo] = React.useState(() => dateOnly(searchParams.get('to')) ?? defaultRange.to);
  const [appliedRange, setAppliedRange] = React.useState(() => ({ from: draftFrom, to: draftTo }));
  const [sort, setSort] = React.useState<CreativeDecisionPortfolioSort>(() => parseSort(searchParams.get('sort')));
  const view = parseView(searchParams.get('view'));
  const changeView = React.useCallback((nextView: CreativeDecisionsView) => {
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      if (nextView === 'decisions') next.delete('view');
      else next.set('view', nextView);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [deploymentScope, setDeploymentScope] = React.useState<CreativeDeploymentScope>(
    () => (searchParams.get('scope') === 'all' ? 'all' : 'funnel_linked'),
  );
  const [draftFilters, setDraftFilters] = React.useState<CreativeDecisionPortfolioFilters>(() => parseFilters(searchParams));
  const [appliedFilters, setAppliedFilters] = React.useState<CreativeDecisionPortfolioFilters>(() => parseFilters(searchParams));
  const [search, setSearch] = React.useState(() => searchParams.get('q') ?? '');
  const localeFilter = parseReportLocaleFilter(searchParams.get('locale'));
  const [portfolio, setPortfolio] = React.useState<CreativeDecisionPortfolioResult | null>(null);
  const [learningReport, setLearningReport] = React.useState<CreativeLearningOperationsReport | null>(null);
  const [learningLoading, setLearningLoading] = React.useState(true);
  const [learningError, setLearningError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [loadMoreError, setLoadMoreError] = React.useState<string | null>(null);
  const [detailDeploymentKey, setDetailDeploymentKey] = React.useState<string | null>(null);
  const [detailDecision, setDetailDecision] = React.useState<CreativeDecisionProjection | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [stopReviewDecision, setStopReviewDecision] = React.useState<CreativeDecisionProjection | null>(null);
  const [providerSourceRevision, setProviderSourceRevision] = React.useState(0);
  const client = React.useMemo(() => new FunnelInsightsApiClient(), []);
  const loadMoreControllerRef = React.useRef<AbortController | null>(null);
  const detailControllerRef = React.useRef<AbortController | null>(null);
  const refreshControllerRef = React.useRef<AbortController | null>(null);
  const request = React.useMemo<CreativeDecisionPortfolioRequest>(() => ({
    projectId,
    from: startOfDayIso(appliedRange.from),
    to: endExclusiveIso(appliedRange.to),
    ...appliedFilters,
    deploymentScope,
    sort,
    limit: 100,
  }), [appliedFilters, appliedRange.from, appliedRange.to, deploymentScope, projectId, sort]);
  const enrichmentDeploymentKeys = React.useMemo(() => Array.from(new Set([
    ...(portfolio?.rows ?? []).map((row) => row.deployment.deploymentKey),
    ...(detailDeploymentKey ? [detailDeploymentKey] : []),
  ])), [detailDeploymentKey, portfolio?.rows]);
  const enrichmentScopeKey = React.useMemo(
    () => JSON.stringify({ projectId, request }),
    [projectId, request],
  );
  const creativeEnrichment = useCreativeReportEnrichment({
    client,
    projectId,
    deploymentKeys: enrichmentDeploymentKeys,
    enabled: view === 'decisions',
    scopeKey: enrichmentScopeKey,
  });
  const providerSourceRows = React.useMemo(() => {
    const rows = [...(portfolio?.rows ?? [])];
    if (detailDecision && !rows.some((row) => (
      row.deployment.deploymentKey === detailDecision.deployment.deploymentKey
    ))) {
      rows.push(detailDecision);
    }
    return rows;
  }, [detailDecision, portfolio?.rows]);
  const providerSourceEnrichment = useCreativeProviderSourceEnrichment({
    client,
    projectId,
    rows: providerSourceRows,
    enabled: view === 'decisions',
    scopeKey: `${enrichmentScopeKey}:${providerSourceRevision}`,
  });
  const handleProviderSourceSynced = React.useCallback(() => {
    setProviderSourceRevision((current) => current + 1);
  }, []);

  React.useEffect(() => {
    if (!projectId) return;
    // W-R6: a saved view (performance/diagnostics) runs ONLY its own lane —
    // the portfolio + learning report load exclusively on the Decisions view.
    if (view !== 'decisions') return;
    const controller = new AbortController();
    loadMoreControllerRef.current?.abort();
    detailControllerRef.current?.abort();
    refreshControllerRef.current?.abort();
    setPortfolio(null);
    setLearningReport(null);
    setLearningLoading(true);
    setLearningError(null);
    setLoading(true);
    setError(null);
    setLoadingMore(false);
    setLoadMoreError(null);
    setDetailDeploymentKey(null);
    setDetailDecision(null);
    setDetailLoading(false);
    setDetailError(null);
    setStopReviewDecision(null);
    client.getCreativeDecisions({ ...request, signal: controller.signal }).then((response) => {
      setPortfolio(response.data);
    }).catch((reason) => {
      if (controller.signal.aborted) return;
      setError(reason instanceof Error ? reason.message : String(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    client.getCreativeLearningOperationsReport({
      projectId,
      from: request.from,
      to: request.to,
      signal: controller.signal,
    }).then((response) => {
      setLearningReport(response.data);
    }).catch((reason) => {
      if (controller.signal.aborted) return;
      setLearningError(errorMessage(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLearningLoading(false);
    });
    return () => controller.abort();
  }, [client, projectId, request, view]);

  React.useEffect(() => () => {
    loadMoreControllerRef.current?.abort();
    detailControllerRef.current?.abort();
    refreshControllerRef.current?.abort();
  }, []);

  const changeDeploymentScope = React.useCallback((nextScope: CreativeDeploymentScope) => {
    setDeploymentScope(nextScope);
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (nextScope === 'all') next.set('scope', 'all');
      else next.delete('scope');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const applyRange = React.useCallback(() => {
    if (draftFrom > draftTo) {
      setError('From must be on or before To.');
      return;
    }
    setAppliedRange({ from: draftFrom, to: draftTo });
    updateSearch(setSearchParams, draftFrom, draftTo, sort, appliedFilters, search);
  }, [appliedFilters, draftFrom, draftTo, search, setSearchParams, sort]);

  const changeSort = React.useCallback((nextSort: CreativeDecisionPortfolioSort) => {
    setSort(nextSort);
    updateSearch(setSearchParams, appliedRange.from, appliedRange.to, nextSort, appliedFilters, search);
  }, [appliedFilters, appliedRange, search, setSearchParams]);

  const applyFilters = React.useCallback(() => {
    const normalized = normalizeFilters(draftFilters);
    setDraftFilters(normalized);
    setAppliedFilters(normalized);
    updateSearch(setSearchParams, appliedRange.from, appliedRange.to, sort, normalized, search);
  }, [appliedRange.from, appliedRange.to, draftFilters, search, setSearchParams, sort]);

  const clearFilters = React.useCallback(() => {
    const cleared = cloneEmptyFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    updateSearch(setSearchParams, appliedRange.from, appliedRange.to, sort, cleared, search);
  }, [appliedRange.from, appliedRange.to, search, setSearchParams, sort]);

  const changeSearch = React.useCallback((nextSearch: string) => {
    setSearch(nextSearch);
    updateSearch(setSearchParams, appliedRange.from, appliedRange.to, sort, appliedFilters, nextSearch);
  }, [appliedFilters, appliedRange.from, appliedRange.to, setSearchParams, sort]);

  const changeLocaleFilter = React.useCallback((nextLocale: string) => {
    const normalized = parseReportLocaleFilter(nextLocale);
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (normalized === CREATIVE_REPORT_LOCALE_ALL) next.delete('locale');
      else next.set('locale', normalized);
      // A user-driven filter change ends the exact-rendition deep-link
      // intent; retaining these pins would make the URL claim two different
      // locale identities.
      next.delete('deployment');
      next.delete('rendition');
      next.delete('exportManifest');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const refreshPortfolio = React.useCallback(() => {
    refreshControllerRef.current?.abort();
    const controller = new AbortController();
    refreshControllerRef.current = controller;
    setLoading(true);
    setLearningLoading(true);
    setError(null);
    setLearningError(null);
    setLoadMoreError(null);
    client.getCreativeDecisions({ ...request, signal: controller.signal }).then((response) => {
      if (!controller.signal.aborted) setPortfolio(response.data);
    }).catch((reason) => {
      if (!controller.signal.aborted) setError(errorMessage(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    client.getCreativeLearningOperationsReport({
      projectId,
      from: request.from,
      to: request.to,
      signal: controller.signal,
    }).then((response) => {
      if (!controller.signal.aborted) setLearningReport(response.data);
    }).catch((reason) => {
      if (!controller.signal.aborted) setLearningError(errorMessage(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLearningLoading(false);
    });
  }, [client, projectId, request, view]);

  /**
   * Manual half of the cached-first hybrid (topup plan S1): exactly one POST
   * (server recomputes + persists the snapshot) followed by exactly one GET
   * (validation runs on the read path). Served numbers stay on screen while
   * the refresh is in flight — the banner button carries the spinner.
   */
  const refreshSnapshot = React.useCallback(async () => {
    await client.refreshCreativeDecisionsPortfolioSnapshot({ projectId });
    const response = await client.getCreativeDecisions({ ...request });
    setPortfolio(response.data);
  }, [client, projectId, request, view]);

  /**
   * I5b banner state-2 CTA: re-sync Meta delivery for EVERY account visible
   * in the portfolio over the applied window (rows re-bind to the active
   * registered money policy via the server's registry auto-resolve), then
   * re-read the portfolio so the banner state reflects the new bindings.
   * Sequential on purpose — one provider sync at a time.
   */
  const resyncDelivery = React.useCallback(async () => {
    const accountIds = [...new Set(
      (portfolio?.rows ?? [])
        .filter((row) => row.deployment.provider === 'meta')
        .map((row) => row.deployment.providerAccountId),
    )];
    if (accountIds.length === 0) return;
    for (const adAccountId of accountIds) {
      await client.resyncMetaAdsDelivery({
        projectId,
        adAccountId,
        since: appliedRange.from,
        until: appliedRange.to,
      });
    }
    const response = await client.getCreativeDecisions({ ...request });
    setPortfolio(response.data);
  }, [appliedRange.from, appliedRange.to, client, portfolio, projectId, request]);

  const loadMore = React.useCallback(() => {
    const cursor = portfolio?.page.nextCursor;
    if (!cursor || loadingMore) return;
    loadMoreControllerRef.current?.abort();
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setLoadingMore(true);
    setLoadMoreError(null);
    client.getCreativeDecisions({ ...request, cursor, signal: controller.signal }).then((response) => {
      if (controller.signal.aborted) return;
      if (
        portfolio
        && (response.data.scopeRef !== portfolio.scopeRef || response.data.projectionVersion !== portfolio.projectionVersion)
      ) {
        throw new Error('Next page identity does not match the loaded portfolio.');
      }
      setPortfolio((current) => current ? appendPortfolioPage(current, response.data) : response.data);
    }).catch((reason) => {
      if (!controller.signal.aborted) setLoadMoreError(errorMessage(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingMore(false);
    });
  }, [client, loadingMore, portfolio?.page.nextCursor, request]);

  const loadDetail = React.useCallback((deploymentKey: string) => {
    detailControllerRef.current?.abort();
    const controller = new AbortController();
    detailControllerRef.current = controller;
    setDetailDeploymentKey(deploymentKey);
    setDetailDecision(null);
    setDetailLoading(true);
    setDetailError(null);
    client.getCreativeDecision({
      projectId,
      deploymentKey,
      from: request.from,
      to: request.to,
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      if (response.data.deployment.deploymentKey !== deploymentKey) {
        throw new Error('Detail identity does not match the selected deployment.');
      }
      setDetailDecision(response.data);
    }).catch((reason) => {
      if (!controller.signal.aborted) setDetailError(errorMessage(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setDetailLoading(false);
    });
  }, [client, projectId, request.from, request.to]);

  // I3 deep link (?deployment=…): resolve the drawer through the UNSCOPED
  // detail endpoint on first mount — the chip must open its drawer regardless
  // of the scope toggle or which portfolio page the row lives on.
  const deeplinkDeploymentKey = React.useMemo(
    () => searchParams.get('deployment') ?? undefined,
    // Snapshot once — later in-app param churn must not re-trigger the drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const deeplinkOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (!deeplinkDeploymentKey || deeplinkOpenedRef.current || !projectId) return;
    deeplinkOpenedRef.current = true;
    loadDetail(deeplinkDeploymentKey);
  }, [deeplinkDeploymentKey, loadDetail, projectId]);

  const closeDetail = React.useCallback(() => {
    detailControllerRef.current?.abort();
    setDetailDeploymentKey(null);
    setDetailDecision(null);
    setDetailLoading(false);
    setDetailError(null);
  }, []);

  const handlePrimaryAction = React.useCallback((decision: CreativeDecisionProjection) => {
    const action = decision.recommendation.primaryAction;
    const target = decision.recommendation.actionTarget;
    const sourceParams = buildSourceHandoffParams(decision, appliedRange);
    if (action === 'create_refresh_experiment'
      && target?.kind === 'refresh_draft'
      && target.mode === 'refresh-experiment-draft') {
      sourceParams.set('mode', target.mode);
      navigate(`/project/${encodeURIComponent(projectId)}/ad-creative?${sourceParams.toString()}`);
      return;
    }
    if ((action === 'review_scale_probe' || action === 'review_next_read')
      && target?.kind === 'experiment_draft'
      && decision.decisionSupport?.nextExperiment?.status === 'available') {
      appendExperimentHandoffParams(sourceParams, decision);
      sourceParams.set('mode', target.mode);
      navigate(`/project/${encodeURIComponent(projectId)}/experiments?${sourceParams.toString()}`);
      return;
    }
    if (action === 'review_stop_evidence' && target?.kind === 'stop_evidence_review') {
      setStopReviewDecision(decision);
      return;
    }
    const source = target?.kind === 'source_link'
      ? unique(decision.sourceLinks?.filter((link) => link.kind === target.sourceLinkKind) ?? [])[0]
      : undefined;
    const matchingSourceCount = target?.kind === 'source_link'
      ? decision.sourceLinks?.filter((link) => link.kind === target.sourceLinkKind).length ?? 0
      : 0;
    if (matchingSourceCount !== 1) {
      setError('This action has no single server-verified destination. Refresh the evidence before continuing.');
      return;
    }
    if (source?.external) {
      window.open(source.href, '_blank', 'noopener,noreferrer');
    } else if (source) {
      navigate(source.href);
    }
  }, [appliedRange, navigate, projectId]);

  const askEvidence = React.useCallback((input: {
    snapshotId: string;
    projectionHash: string;
    question: string;
    signal?: AbortSignal;
  }) => client.answerCreativeDecisionCopilot({ projectId, ...input }), [client, projectId]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1720, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2.5 }}>
      <Stack spacing={2.5}>
        <Box>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link component="button" underline="hover" onClick={() => navigate(`/project/${projectId}/reports`)}>
              Reports
            </Link>
            <Typography color="text.primary">Creative decisions</Typography>
          </Breadcrumbs>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>Creative decisions</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 820 }}>
                See which provider deployments currently consume the most money, what value has returned, and which evidence is strong enough for the next reviewed action.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                type="date"
                label="From"
                value={draftFrom}
                onChange={(event) => setDraftFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={draftTo}
                onChange={(event) => setDraftTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" onClick={applyRange}>Apply</Button>
              {view === 'decisions' && (
                <Button startIcon={<RefreshIcon />} onClick={refreshPortfolio} disabled={loading}>Refresh</Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* W-R6 (DISC-AC-149/150): saved-view tabs — the destinations of the
            deleted standalone Reports sections. Deep-linkable via ?view=. */}
        <Tabs
          value={view}
          onChange={(_event, nextView: CreativeDecisionsView) => changeView(nextView)}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.viewTabs}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Decisions" value="decisions" data-testid={CREATIVE_DECISIONS_TEST_IDS.viewTab('decisions')} />
          <Tab label="Performance" value="performance" data-testid={CREATIVE_DECISIONS_TEST_IDS.viewTab('performance')} />
          <Tab label="Diagnostics" value="diagnostics" data-testid={CREATIVE_DECISIONS_TEST_IDS.viewTab('diagnostics')} />
        </Tabs>

        {view === 'performance' && (
          <CreativePerformanceSavedView
            client={client}
            projectId={projectId}
            from={appliedRange.from}
            to={appliedRange.to}
          />
        )}
        {view === 'diagnostics' && (
          <CreativeDiagnosticsSavedView
            client={client}
            projectId={projectId}
            from={appliedRange.from}
            to={appliedRange.to}
          />
        )}

        {view === 'decisions' && (
        <CreativeDecisionPortfolio
          portfolio={portfolio}
          loading={loading}
          error={error}
          sort={sort}
          onSortChange={changeSort}
          deploymentScope={portfolio?.sourceCoverage.deploymentScope ?? deploymentScope}
          onDeploymentScopeChange={changeDeploymentScope}
          search={search}
          onSearchChange={changeSearch}
          draftFilters={draftFilters}
          appliedFilterCount={countFilters(appliedFilters)}
          onFiltersChange={setDraftFilters}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
          loadingMore={loadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={loadMore}
          detailDecision={detailDeploymentKey ? detailDecision : null}
          detailLoading={detailLoading}
          detailError={detailError}
          enrichmentByDeploymentKey={creativeEnrichment.byDeploymentKey}
          enrichmentLoading={creativeEnrichment.loading}
          enrichmentError={creativeEnrichment.error}
          providerSourceByDeploymentKey={providerSourceEnrichment.byCorrelationKey}
          providerSourceLoading={providerSourceEnrichment.loading}
          providerSourceError={providerSourceEnrichment.error}
          projectId={projectId}
          providerSourceAssociationWindow={{ from: request.from.slice(0, 10), to: request.to.slice(0, 10) }}
          onProviderSourceSynced={handleProviderSourceSynced}
          sourceSyncClient={client}
          localeFilter={localeFilter}
          onLocaleFilterChange={changeLocaleFilter}
          onOpenDecision={loadDetail}
          onRetryDetail={() => detailDeploymentKey && loadDetail(detailDeploymentKey)}
          onCloseDecision={closeDetail}
          onRetry={refreshPortfolio}
          onConnectAdAccount={() => navigate(`/project/${encodeURIComponent(projectId)}/integrations`)}
          onPrimaryAction={handlePrimaryAction}
          onAskEvidence={askEvidence}
          onRefreshSnapshot={refreshSnapshot}
          onResyncDelivery={resyncDelivery}
          deeplinkDeploymentKey={deeplinkDeploymentKey}
        />
        )}

        {view === 'decisions' && (
        <CreativeLearningOperationsSummary
          report={learningReport}
          loading={learningLoading}
          error={learningError}
        />
        )}

        <Dialog
          open={Boolean(stopReviewDecision)}
          onClose={() => setStopReviewDecision(null)}
          maxWidth="sm"
          fullWidth
          data-testid={CREATIVE_DECISIONS_TEST_IDS.stopEvidenceReview}
        >
          <DialogTitle>Stop-evidence review</DialogTitle>
          <DialogContent dividers>
            {stopReviewDecision && (
              <Stack spacing={2}>
                <Alert severity="warning">
                  This is a read-only evidence review. Closing it does not pause an ad, change spend,
                  or create an experiment.
                </Alert>
                <Box>
                  <Typography variant="subtitle2">Exact deployment</Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {stopReviewDecision.deployment.deploymentKey}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Decision evidence</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    {stopReviewDecision.recommendation.decisionMetricKeys.map((metric) => (
                      <Chip key={metric} label={metric} size="small" />
                    ))}
                    {!stopReviewDecision.recommendation.decisionMetricKeys.length && (
                      <Chip label="No decision-grade metric is currently bound" size="small" color="warning" />
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Immutable source revisions</Typography>
                  {stopReviewDecision.freshness.sourceRevisionRefs.map((ref) => (
                    <Typography key={ref} variant="caption" display="block" sx={{ overflowWrap: 'anywhere' }}>
                      {ref}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setStopReviewDecision(null)}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.stopEvidenceReviewClose}
            >
              Close review
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 29 * 24 * 60 * 60 * 1_000);
  return { from: isoDate(from), to: isoDate(to) };
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function dateOnly(value: string | null): string | null {
  if (!value) return null;
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match?.[0] ?? null;
}

function startOfDayIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

function endExclusiveIso(date: string): string {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString();
}

function parseSort(value: string | null): CreativeDecisionPortfolioSort {
  return value === 'needs_action' || value === 'historical_spend' ? value : 'money_flow';
}

function updateSearch(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  from: string,
  to: string,
  sort: CreativeDecisionPortfolioSort,
  filters: CreativeDecisionPortfolioFilters,
  search: string,
): void {
  const params = new URLSearchParams({ from, to, sort });
  appendListParam(params, 'provider', filters.providers);
  appendListParam(params, 'providerAccountId', filters.providerAccountIds);
  appendListParam(params, 'lifecycleStage', filters.lifecycleStages);
  appendListParam(params, 'dataState', filters.dataStates);
  appendListParam(params, 'decision', filters.decisions);
  appendListParam(params, 'evidenceClass', filters.evidenceClasses);
  appendListParam(params, 'origin', filters.origins);
  appendListParam(params, 'campaignFamily', filters.campaignFamilies);
  appendListParam(params, 'network', filters.networks);
  appendListParam(params, 'funnel', filters.funnels);
  appendListParam(params, 'geo', filters.geos);
  appendListParam(params, 'purchaseMode', filters.purchaseModes);
  appendListParam(params, 'subscriptionEntry', filters.subscriptionEntries);
  appendListParam(params, 'productId', filters.productIds);
  appendListParam(params, 'planId', filters.planIds);
  appendListParam(params, 'priceId', filters.priceIds);
  appendListParam(params, 'originalCurrency', filters.originalCurrencies);
  appendListParam(params, 'reportingCurrency', filters.reportingCurrencies);
  appendListParam(params, 'maturity', filters.maturityStates);
  appendListParam(params, 'currency', filters.currencies);
  if (search.trim()) params.set('q', search.trim());
  setSearchParams((previous) => {
    // W-R6: the saved-view axis (and the scope chip) survive a range/sort/
    // filter update — updateSearch rebuilds the param set from scratch.
    const view = previous.get('view');
    if (view) params.set('view', view);
    const scope = previous.get('scope');
    if (scope) params.set('scope', scope);
    for (const key of ['locale', 'deployment', 'rendition', 'exportManifest']) {
      const value = previous.get(key);
      if (value) params.set(key, value);
    }
    return params;
  }, { replace: true });
}

function parseFilters(searchParams: URLSearchParams): CreativeDecisionPortfolioFilters {
  return normalizeFilters({
    providers: parseEnumList(searchParams, 'provider', ['meta', 'google_ads', 'tiktok_ads']),
    providerAccountIds: parseList(searchParams, 'providerAccountId'),
    lifecycleStages: parseEnumList(searchParams, 'lifecycleStage', [
      'hypothesis', 'produced', 'launched', 'learning', 'mature', 'scaling', 'fatiguing', 'refreshed', 'archived',
    ]),
    dataStates: parseEnumList(searchParams, 'dataState', [
      'available', 'no_data', 'too_young', 'unreconciled', 'pending_settlement', 'stale', 'blocked_by_trust',
    ]),
    decisions: parseEnumList(searchParams, 'decision', [
      'observe', 'hold', 'investigate', 'controlled_scale', 'refresh_due', 'stop_candidate', 'archived',
    ]),
    evidenceClasses: parseEnumList(searchParams, 'evidenceClass', [
      'descriptive', 'predictive', 'randomized', 'incremental',
    ]),
    origins: parseEnumList(searchParams, 'origin', ['segmently_created', 'connected_account', 'unknown']),
    campaignFamilies: parseList(searchParams, 'campaignFamily'),
    networks: parseList(searchParams, 'network'),
    funnels: parseList(searchParams, 'funnel'),
    geos: parseList(searchParams, 'geo'),
    purchaseModes: parseEnumList(searchParams, 'purchaseMode', ['one_time', 'subscription', 'unknown']),
    subscriptionEntries: parseEnumList(searchParams, 'subscriptionEntry', [
      'none', 'no_trial', 'free_trial', 'paid_trial', 'unknown',
    ]),
    productIds: parseList(searchParams, 'productId'),
    planIds: parseList(searchParams, 'planId'),
    priceIds: parseList(searchParams, 'priceId'),
    originalCurrencies: parseList(searchParams, 'originalCurrency'),
    reportingCurrencies: parseList(searchParams, 'reportingCurrency'),
    maturityStates: parseEnumList(searchParams, 'maturity', ['immature', 'mature', 'unknown']),
    currencies: parseList(searchParams, 'currency'),
  });
}

function parseReportLocaleFilter(value: string | null): string {
  const normalized = value?.trim() ?? '';
  if (!normalized || normalized === CREATIVE_REPORT_LOCALE_ALL) return CREATIVE_REPORT_LOCALE_ALL;
  if (normalized === 'unknown') return normalized;
  return /^[A-Za-z0-9]{1,8}(?:-[A-Za-z0-9]{1,8})*$/.test(normalized)
    ? normalized
    : CREATIVE_REPORT_LOCALE_ALL;
}

function normalizeFilters(filters: CreativeDecisionPortfolioFilters): CreativeDecisionPortfolioFilters {
  const values = { ...EMPTY_CREATIVE_DECISION_PORTFOLIO_FILTERS, ...filters };
  return {
    providers: unique(values.providers),
    providerAccountIds: unique(values.providerAccountIds.map((value) => value.trim()).filter(Boolean)),
    lifecycleStages: unique(values.lifecycleStages),
    dataStates: unique(values.dataStates),
    decisions: unique(values.decisions),
    evidenceClasses: unique(values.evidenceClasses),
    origins: unique(values.origins),
    campaignFamilies: normalizeIds(values.campaignFamilies),
    networks: normalizeIds(values.networks),
    funnels: normalizeIds(values.funnels),
    geos: normalizeIds(values.geos),
    purchaseModes: unique(values.purchaseModes),
    subscriptionEntries: unique(values.subscriptionEntries),
    productIds: normalizeIds(values.productIds),
    planIds: normalizeIds(values.planIds),
    priceIds: normalizeIds(values.priceIds),
    originalCurrencies: normalizeCurrencies(values.originalCurrencies),
    reportingCurrencies: normalizeCurrencies(values.reportingCurrencies),
    maturityStates: unique(values.maturityStates),
    currencies: normalizeCurrencies(values.currencies),
  };
}

function cloneEmptyFilters(): CreativeDecisionPortfolioFilters {
  return Object.fromEntries(
    Object.keys(EMPTY_CREATIVE_DECISION_PORTFOLIO_FILTERS).map((key) => [key, []]),
  ) as unknown as CreativeDecisionPortfolioFilters;
}

function parseList(searchParams: URLSearchParams, key: string): string[] {
  return unique((searchParams.get(key) ?? '').split(',').map((value) => value.trim()).filter(Boolean));
}

function parseEnumList<T extends string>(
  searchParams: URLSearchParams,
  key: string,
  allowed: readonly T[],
): T[] {
  const allow = new Set<string>(allowed);
  return parseList(searchParams, key).filter((value): value is T => allow.has(value));
}

function appendListParam(params: URLSearchParams, key: string, values: readonly string[]): void {
  if (values.length) params.set(key, values.join(','));
}

function countFilters(filters: CreativeDecisionPortfolioFilters): number {
  return Object.values(filters).reduce((sum, values) => sum + values.length, 0);
}

function appendPortfolioPage(
  current: CreativeDecisionPortfolioResult,
  next: CreativeDecisionPortfolioResult,
): CreativeDecisionPortfolioResult {
  if (current.scopeRef !== next.scopeRef || current.projectionVersion !== next.projectionVersion) return current;
  const seen = new Set(current.rows.map((row) => row.deployment.deploymentKey));
  const appended = next.rows.filter((row) => {
    const key = row.deployment.deploymentKey;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return {
    ...next,
    rows: [...current.rows, ...appended],
  };
}

export function buildSourceHandoffParams(
  decision: CreativeDecisionProjection,
  sourceWindow: { from: string; to: string },
): URLSearchParams {
  const params = new URLSearchParams({
    sourceHandoffVersion: '1',
    sourceDraftCatalog: 'segmently-provider-neutral-v1',
    sourceProviderMutationAllowed: 'false',
    sourceReviewRequired: 'true',
    sourceFrom: sourceWindow.from,
    sourceTo: sourceWindow.to,
    sourceDeployment: decision.deployment.deploymentKey,
    sourceProjection: decision.projectionId,
    sourceProjectionVersion: decision.projectionVersion,
    sourceScope: decision.scopeRef,
    sourceProvider: decision.deployment.provider,
    sourceProviderAccount: decision.deployment.providerAccountId,
    sourceProviderResource: decision.deployment.providerResourceName,
    sourceCapabilitySnapshot: decision.capability.capabilitySnapshotId,
    sourceDecision: decision.recommendation.decision,
    sourcePrimaryAction: decision.recommendation.primaryAction,
    sourceAsOf: decision.freshness.asOf,
  });
  const revisions = decision.freshness.sourceRevisionRefs.length
    ? decision.freshness.sourceRevisionRefs
    : [`creative-decision-projection:${decision.projectionId}`];
  [...revisions].sort().forEach((revision) => params.append('sourceRevision', revision));
  decision.artifactSnapshots
    .map((artifact) => artifact.artifactSnapshotKey)
    .sort()
    .forEach((artifact) => params.append('sourceArtifactSnapshot', artifact));
  [...decision.recommendation.decisionMetricKeys].sort()
    .forEach((metric) => params.append('sourceDecisionMetric', metric));
  [...new Set(decision.evidence.flatMap((binding) => binding.sourceRefs))].sort()
    .forEach((ref) => params.append('sourceEvidenceRef', ref));
  return params;
}

/** Adds the Experiment Registry-only protocol to the common source envelope.
 * Refresh destinations intentionally receive only their own exact allowlist. */
function appendExperimentHandoffParams(
  params: URLSearchParams,
  decision: CreativeDecisionProjection,
): void {
  const nextExperiment = decision.decisionSupport?.nextExperiment;
  if (nextExperiment?.status === 'available') {
    if (nextExperiment.estimandRef) params.set('sourceEstimand', nextExperiment.estimandRef);
    if (nextExperiment.primaryMetricRef) params.set('sourcePrimaryMetric', nextExperiment.primaryMetricRef);
    if (nextExperiment.minimumDetectableEffect !== undefined) {
      params.set('sourceMde', String(nextExperiment.minimumDetectableEffect));
    }
    if (nextExperiment.minimumDurationDays !== undefined) {
      params.set('sourceMinimumDurationDays', String(nextExperiment.minimumDurationDays));
    }
    if (nextExperiment.stoppingPolicyRef) {
      params.set('sourceStoppingPolicy', nextExperiment.stoppingPolicyRef);
    }
    [...(nextExperiment.guardrailMetricRefs ?? [])].sort()
      .forEach((metric) => params.append('sourceGuardrail', metric));
    [...(nextExperiment.fixedFactors ?? [])].sort()
      .forEach((factor) => params.append('sourceFixedFactor', factor));
    [...(nextExperiment.factorsToVary ?? [])].sort()
      .forEach((factor) => params.append('sourceFactorToVary', factor));
  }
  const surrogate = decision.decisionSupport?.validatedSurrogate;
  params.set('sourceSurrogateStatus', surrogate?.status ?? 'unavailable');
  [...(surrogate?.reasonCodes ?? ['validated_treatment_surrogate_unavailable'])].sort()
    .forEach((reason) => params.append('sourceSurrogateReason', reason));
  [...(surrogate?.evidenceRefs ?? [])].sort()
    .forEach((ref) => params.append('sourceSurrogateEvidence', ref));
  if (surrogate?.metricDefinitionRef) params.set('sourceSurrogateMetric', surrogate.metricDefinitionRef);
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function normalizeIds(values: readonly string[]): string[] {
  return unique(values.map((value) => value.trim()).filter(Boolean));
}

function normalizeCurrencies(values: readonly string[]): string[] {
  return unique(values
    .map((value) => value.trim().toUpperCase())
    .filter((value) => /^[A-Z]{3}$/.test(value)));
}

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}
